// R5 — `RubricReadPublicV1`: PROJECTION BY DELETION over the engine's real serialized output (spec §1.2a), plus
// the §1.2b PROVENANCE MAP that governs how each payload path is rendered and scanned.
//
// THE ONE RULE THAT MAKES THIS FILE DIFFERENT FROM ITS FOUR PREDECESSORS. Every earlier draft of this DTO
// RE-AUTHORED the payload as a parallel hand-written schema, checked against prose recall of the engine's real
// types rather than against the types themselves — and every review pass found more divergences that way
// (overlay shapes, prevalence slot names, dropped entry fields, an invented non-null `overall_band` the engine
// cannot produce). This file does not re-author anything. It takes the engine's actual output, DELETES an
// enumerated deny-list, ATTACHES an enumerated add-list, and ships everything else verbatim:
//
//     RubricReadPublicV1 = engine output − DENY_LIST + ADD_LIST
//
// The deletions are written as rest-destructures (`const { trace, ...rest } = d`), so "every field not named in
// the deny-list ships exactly as the engine emits it" is a property of the CODE, not of a field list somebody has
// to remember to update. A new engine field reaches the surface automatically; that is intended.
//
// TWO SIDES, ONE BOUNDARY (§1.2a's storage-boundary rule):
//   * STORED  — everything above, written into `rubric_reads.public_json` at COMPUTE time and immutable after.
//               Includes `profile_release_id` per overlay row, because it is `rubric_reads.overlay_release_id`
//               AT THE TIME THIS ROW WAS COMPUTED: fixed for this row forever, exactly like the identity columns.
//   * SERVE   — `freshness`, `stale_reason`, and (rev-14 amendment) `overlay_status`. These are the fields that
//               describe a RELATIONSHIP between an immutable stored row and the CURRENT canonical state, so they
//               cannot be fixed at write time without lying the moment inputs move. Nothing else joins them.
import type {
  CvAssessment,
  DomainAssessment,
  EntryAssessment,
  FacetDecision,
  ContinuityDecision,
  ConfidenceDecision,
  ResponsibilityLevel,
  CompletionLevel,
  DomainId,
  ContentBand,
} from './rubricEngine';
import type { SpecialtyOverlay, OverlayRow } from './specialtyOverlay';
import type { SpecialtyConventionPanel } from './specialtyConvention';
import type { PresentationAssessment, SubElementAssessment } from './presentationBand';
import type { Recommendation } from './recommendations';
import type { InputSurface } from './rubricCopy';

/* ============================================================================================================== *
 * PART 1 — THE PROJECTED TYPES (T-DTO-1 part (a)'s subject: an Omit<> chain over the REAL imported interfaces,
 * never a re-declared parallel interface. A drift between these and the engine is a `tsc` failure, before any
 * test runs.)
 * ============================================================================================================== */

/**
 * A facet, post-projection. The WHOLE `trace` object is denied — `rule_id` is an internal decision-rule
 * identifier the engine's own doc comment says must never reach the surface, and shipping a PARTIAL `RuleTrace`
 * (rule_id stripped, evidence_fact_ids left in place) would put the same evidence on the payload twice, beside
 * the add-list sibling. One evidence path, no `trace` key at all.
 */
export type ProjectedFacet<L> = Omit<FacetDecision<L>, 'trace'> & { evidence_fact_ids: string[] };
export type ProjectedContinuity = Omit<ContinuityDecision, 'trace'> & { evidence_fact_ids: string[] };

/** Confidence, post-projection: `{level, copy_string_id}`. `rules_fired` is internal — "never displayed". */
export type ProjectedConfidence = Omit<ConfidenceDecision, 'rules_fired'>;

export type ProjectedEntry = Omit<
  EntryAssessment,
  'responsibility' | 'completion' | 'continuity' | 'confidence' | 'unassessed_signals'
> & {
  responsibility: ProjectedFacet<ResponsibilityLevel>;
  completion: ProjectedFacet<CompletionLevel>;
  continuity: ProjectedContinuity;
  confidence: ProjectedConfidence;
};

/**
 * A domain, post-projection. `trace` is denied WHOLE and its `evidence_fact_ids` reappear as the sibling below.
 *
 * The evidence is NOT recoverable from the unit-id lists, and the belief that it was is a defect this projection
 * exists to avoid repeating: `fully_documented_unit_ids`/`assigned_unit_ids` are unit IDENTIFIERS (a unit id may
 * be a `work_id`), while the actual evidentiary FACT ids are assembled separately — and for a Substantial band the
 * trace uniquely carries BOTH the D1/D2/D3 facts of the fully documented units AND the breadth facts, which no
 * unit-id list can reconstruct. The projection copies the array VERBATIM; it never filters or re-derives it.
 */
export type ProjectedDomain = Omit<DomainAssessment, 'trace'> & { evidence_fact_ids: string[] };

/**
 * An overlay row, post-projection.
 *
 * `criterion_label` is denied as a REDUNDANT SIBLING, not because its content is kept off the surface: the engine's
 * own `renderPrevalence` has already filled `{criterion}` from this exact field into `prevalence.text` before this
 * projection ever runs, so the label's content reaches the client either way. Carrying it a second time, unrendered,
 * is duplication — and the label's copy authority is the owner-reviewed profile release regardless of which field
 * carries it, so nothing is lost.
 *
 * `profile_release_id` is attached PER ROW (there is no bare top-level field of that name on this payload).
 */
export type ProjectedOverlayRow = Omit<OverlayRow, 'criterion_label'> & { profile_release_id: string };
export type ProjectedOverlay = Omit<SpecialtyOverlay, 'rows'> & { rows: ProjectedOverlayRow[] };

/** A presentation sub-element, post-projection: `rule_id` is "Internal, never displayed" per its own doc comment. */
export type ProjectedSubElement = Omit<SubElementAssessment, 'rule_id'>;
export type ProjectedPresentation = Omit<PresentationAssessment, 'overall_rule_id' | 'sub_elements'> & {
  sub_elements: ProjectedSubElement[];
};

export type ProjectedAssessment = Omit<CvAssessment, 'domains' | 'entries' | 'review_confidence'> & {
  domains: ProjectedDomain[];
  entries: ProjectedEntry[];
  review_confidence: ProjectedConfidence;
};

/**
 * THE OVERLAY'S RELATIONSHIP TO THE RELEASE REGISTRY — a TRI-STATE, plus absent (ruling R5-OQ-H, rev 14).
 *
 * WHY IT EXISTS. §4.5 asks the panel to distinguish "a profile that expired/rolled over" from "a specialty that
 * never had a released profile", and to render `CS-PROFILE-EXPIRED` only for the former. `mode` cannot carry that
 * distinction (it is derived from `overlay_release_id` being null, which is equally true of both causes), and the
 * freshness envelope cannot either: `stale_reason` including `overlay_release` means the overlay component MOVED
 * since this read was computed — which is just as true when a profile is released for the FIRST time, so keying
 * the expiry notice on it made the surface say criteria were "previously shown" that never were.
 *
 *   `'current'`     — this read pinned a release, and that release is still the registry's active one.
 *   `'expired'`     — the specialty HAS a profile in the registry, but its cycle has turned (§6.6), so no release
 *                     applies. This is the ONE state that renders `CS-PROFILE-EXPIRED`.
 *   `'superseded'`  — this read pinned a release the registry no longer serves for that specialty (a rollover to
 *                     a different release, or a release withdrawn from review).
 *   `null`          — NO PROFILE WAS EVER INVOLVED: no specialty, the kill switch is off, or the registry holds
 *                     no profile for this specialty at all. Absent, not a state.
 *
 * DERIVED AT **SERVE** TIME (`deriveOverlayStatus`, `rubricCompute.ts`) from the read's own pinned
 * `overlay_release_id` — which IS `rubric_reads.overlay_release_id`, the stored column, and the same value this
 * row's `input_manifest_json.overlay_release_hash` component is built from — compared against the release
 * registry AS IT STANDS NOW, through the single gated loader.
 *
 * WHY SERVE AND NOT PROJECTION (the rev-14 amendment, R5.2 round-2 finding 2). Rev 14 as ratified put this field
 * on the STORED side, derived once at projection time. That made the tri-state unable to represent two of its own
 * three histories:
 *
 *   * `'superseded'` was UNREACHABLE in any persisted production result. A fresh compute can only ever pin the
 *     release the loader is serving at that moment, so its pin always equals the registry's answer — `'current'`,
 *     by construction, every time.
 *   * A read stored as `'current'` stayed `'current'` forever. When its release later expired or rolled over,
 *     nothing revisited the derivation, so §4.5's actual case — "your specialty's profile expired since this
 *     review was computed" — could never render on the reads it was written for.
 *
 * Both are the same defect as storing `freshness`: a relationship between a fixed row and a moving registry,
 * frozen at write time. The pinned release id stays stored (it is a fact about the row); the RELATIONSHIP moves
 * to the envelope. `freshness`'s own reasoning applies verbatim, which is why this field now sits beside it.
 *
 * WITH ZERO RELEASED PROFILES (the shipped `specialtyProfiles.json`) every serve derives `null`, and
 * `CS-PROFILE-EXPIRED` renders nowhere — which is the correct state, not a gap.
 */
export type OverlayStatus = 'current' | 'expired' | 'superseded';

export const OVERLAY_STATUSES: readonly OverlayStatus[] = ['current', 'expired', 'superseded'];

/**
 * The add-list's persistence-identity fields. These are genuinely NOT part of any engine object — the engine
 * computes bands, not row identity — which is why they are listed rather than assumed.
 */
export interface ReadIdentity {
  read_id: string;
  read_revision: number;
  ledger_id: string;
  ledger_version: number;
  correction_revision: number;
  assessed_at: number;
  overlay_release_id: string | null;
  scoring_version: number;
  /**
   * Derived at COMPUTE time from whether `overlay_release_id` is non-null. Deliberately a different thing from the
   * engine's own input-side `mode` field (which its doc comment marks "never read") — this one is output-side.
   */
  mode: 'generic' | 'specialty';
  input_surface: InputSurface;
  /**
   * Projected from the internal `RecomputationLog` at compute time — the log itself never leaves the server, and
   * this narrow band-only projection is the entire public surface of it. Null unless this row resulted from a
   * correction or a refresh.
   */
  band_changes: PublicBandChange[] | null;
}

/**
 * The public band-change row.
 *
 * A DELIBERATE RENAME, and a spec ambiguity resolved rather than glossed. The engine's internal `BandChange` is
 * `{domain, before, after}` (`rubricEngine.ts`), while §1.2a's add-list and §1.2b's provenance map BOTH name this
 * projection's fields `{subject, before_band, after_band}`. That is not a contradiction of the "ship engine types
 * verbatim" rule: `band_changes` is an ADD-LIST PROJECTION out of the internal `RecomputationLog` — which is
 * deny-listed whole and never leaves the server — not a passed-through engine object, so the spec is free to name
 * its fields, and it names them twice, consistently. The spec wins; `subject` carries the `domain` id. Renaming
 * here rather than in the engine keeps the internal log untouched, exactly as the deny-list intends.
 */
export interface PublicBandChange {
  subject: DomainId;
  before_band: ContentBand;
  after_band: ContentBand;
}

/** What `rubric_reads.public_json` holds. Immutable once written. */
export type RubricReadStoredV1 = ProjectedAssessment &
  ReadIdentity & {
    overlay: ProjectedOverlay | null;
    presentation: ProjectedPresentation;
    recommendations: Recommendation[];
    /**
     * R7.1 — the §6b SPECIALTY CONVENTION PANEL, pinned into this read like the overlay beside it.
     *
     * ADDITIVE AND NULLABLE. `null` is the ordinary state (the layer is off, the specialty has no ratified set, or
     * the CV declares none) and every pre-R7 stored row reads as `null` without a migration. It carries NO
     * aggregate of any kind — no documented count, no total, no ordering by status — and it is projected VERBATIM:
     * the module already emits nothing this projection would need to deny.
     */
    convention: SpecialtyConventionPanel | null;
  };

export type Freshness = 'current' | 'stale' | 'corrupt';

/**
 * The ordered `stale_reason` precedence. An array, never a single value: several manifest components can move
 * between one compute and the next request (a SCORING_VERSION bump landing the same week a profile release rolls
 * over), and reporting only the first would understate what changed.
 */
export const STALE_REASON_ORDER = [
  'scoring_version',
  'overlay_release',
  'ledger_changed',
  'input_surface',
  'review_date_config',
] as const;
export type StaleReason = (typeof STALE_REASON_ORDER)[number];

/**
 * The served payload: the stored projection plus the THREE serve-time envelope fields (rev-14 amendment).
 *
 * `overlay_status` joined `freshness`/`stale_reason` here because it is the same KIND of field — a relationship
 * between this immutable row and the registry as it stands at request time. See `OverlayStatus`'s own banner.
 */
export type RubricReadPublicV1 = RubricReadStoredV1 & {
  freshness: Freshness;
  stale_reason: StaleReason[];
  overlay_status: OverlayStatus | null;
};

/* ============================================================================================================== *
 * PART 2 — THE PROJECTION ITSELF
 * ============================================================================================================== */

export interface ProjectionSources {
  assessment: CvAssessment;
  overlay: SpecialtyOverlay | null;
  /** R7.1 §6b. Absent/null ⇒ the stored row's `convention` is `null`. */
  convention?: SpecialtyConventionPanel | null;
  presentation: PresentationAssessment;
  recommendations: Recommendation[];
}

/**
 * `projectRubricRead` — the deny-list/add-list projection, run at COMPUTE time, producing exactly what
 * `public_json` stores.
 *
 * ON THE NAME (a spec ambiguity, resolved here and stated rather than papered over). §1.2a names this function
 * twice with two different signatures: `projectRubricRead(engineOutput, envelope)` for the deny/add projection,
 * and `projectRubricRead(row, currentManifest)` for the serve-time freshness envelope. Those are two different
 * jobs on two different sides of the storage boundary the same section defines, and one function cannot be both
 * without re-deriving stored fields at serve time — which the boundary rule explicitly forbids ("Nothing else is
 * computed at serve time"). Resolved by splitting: THIS function is the compute-time projection, and
 * `serveRubricRead` below is the serve-time envelope. Both names appear in the spec; the boundary decides which
 * behaviour each gets.
 */
export function projectRubricRead(sources: ProjectionSources, identity: ReadIdentity): RubricReadStoredV1 {
  const { assessment, overlay, presentation, recommendations, convention } = sources;

  return {
    ...assessment,
    domains: assessment.domains.map(projectDomain),
    entries: assessment.entries.map(projectEntry),
    review_confidence: projectConfidence(assessment.review_confidence),
    overlay: overlay ? projectOverlay(overlay, identity.overlay_release_id ?? '') : null,
    presentation: projectPresentation(presentation),
    // Recommendations pass through untouched: nothing on `Recommendation` is deny-listed. `template_id` stays
    // nullable, `evidence` stays the real union, `copy_string_id` stays nullable — the shapes the engine emits.
    recommendations,
    // §6b passes through untouched: nothing on the panel is deny-listed, and every string on it is already a
    // registered `{copy_string_id, text}` pair or a ratified registry label.
    convention: convention ?? null,
    ...identity,
  };
}

function projectFacet<L>(f: FacetDecision<L>): ProjectedFacet<L> {
  const { trace, ...rest } = f;
  return { ...rest, evidence_fact_ids: trace.evidence_fact_ids };
}

function projectContinuity(c: ContinuityDecision): ProjectedContinuity {
  const { trace, ...rest } = c;
  return { ...rest, evidence_fact_ids: trace.evidence_fact_ids };
}

function projectConfidence(c: ConfidenceDecision): ProjectedConfidence {
  const { rules_fired, ...rest } = c;
  void rules_fired;
  return rest;
}

function projectEntry(e: EntryAssessment): ProjectedEntry {
  const { responsibility, completion, continuity, confidence, unassessed_signals, ...rest } = e;
  void unassessed_signals; // internal/diagnostic; `signals_incomplete` is the surfaced fact, and it survives above
  return {
    ...rest,
    responsibility: projectFacet(responsibility),
    completion: projectFacet(completion),
    continuity: projectContinuity(continuity),
    confidence: projectConfidence(confidence),
  };
}

function projectDomain(d: DomainAssessment): ProjectedDomain {
  const { trace, ...rest } = d;
  return { ...rest, evidence_fact_ids: trace.evidence_fact_ids };
}

function projectOverlayRow(r: OverlayRow, profile_release_id: string): ProjectedOverlayRow {
  const { criterion_label, ...rest } = r;
  void criterion_label;
  return { ...rest, profile_release_id };
}

function projectOverlay(o: SpecialtyOverlay, profile_release_id: string): ProjectedOverlay {
  return { ...o, rows: o.rows.map((r) => projectOverlayRow(r, profile_release_id)) };
}

function projectSubElement(s: SubElementAssessment): ProjectedSubElement {
  const { rule_id, ...rest } = s;
  void rule_id;
  return rest;
}

function projectPresentation(p: PresentationAssessment): ProjectedPresentation {
  const { overall_rule_id, sub_elements, ...rest } = p;
  void overall_rule_id;
  return { ...rest, sub_elements: sub_elements.map(projectSubElement) };
}

/**
 * The SERVE-TIME envelope — the only fields ever assembled outside `public_json`.
 *
 * `freshness` is a property of the relationship between a stored row and the CURRENT canonical manifest, and that
 * relationship changes after the row is written. Storing it would mean a row that was accurate on Tuesday asserts
 * "current" on Friday, under a scoring version it was never computed under. That is the whole reason for the
 * boundary.
 *
 * `overlay_status` is here for EXACTLY the same reason (rev-14 amendment, R5.2 round-2 finding 2): it compares
 * this row's pinned `overlay_release_id` against the release registry as it stands at request time, and freezing
 * that comparison at write time made `'superseded'` unreachable and left expired reads reporting `'current'`.
 *
 * ALL THREE ARE REQUIRED PARAMETERS, deliberately. A default would let a call site omit the derivation and ship
 * `null` — which is a real value here, meaning "no profile was ever involved" — so an omission would silently
 * become an assertion.
 */
export function serveRubricRead(
  stored: RubricReadStoredV1,
  freshness: Freshness,
  stale_reason: StaleReason[],
  overlay_status: OverlayStatus | null,
): RubricReadPublicV1 {
  return { ...stored, freshness, stale_reason, overlay_status };
}

/* ============================================================================================================== *
 * PART 3 — THE PROVENANCE MAP (§1.2b)
 *
 * NOT a shape the JSON carries. The payload is the engine's projected output verbatim — plain strings, never
 * pre-wrapped in a `{type, ...}` discriminated union — and an earlier draft that required such wrappers
 * contradicted the projection contract directly. What this map is instead: a fixed table from PAYLOAD PATH to
 * provenance, consulted by the client's rendering chokepoint to choose a rendering strategy, and by the
 * banned-output scanner to choose a scanning strategy. The scanner switches on WHICH PATH produced a value, which
 * is a closed enumerable set exactly like the deny-list.
 * ============================================================================================================== */

export type Provenance = 'copy' | 'applicant_verbatim' | 'evidence_reference' | 'opaque_metadata';

/**
 * THE PINNED `applicant_verbatim` SET — exactly two path patterns, no more.
 *
 * This is the T-BAN-1 exemption set, and pinning it here is itself the enforcement mechanism: a system- or
 * profile-authored path cannot enter the exemption without a visible spec diff and a ratification event. No
 * `*_copy_string_id`, `RenderedString`, `SearchScope`, or `SourceReference` path can be added by a code edit.
 *
 * Both patterns are PATH-LOCALLY evidence-bound, which is what makes the exemption ungameable independently of
 * this table: T-VERB-1 requires each value to byte-equal (post-NFC) a fact reached through THAT PATH'S OWN sibling
 * evidence binding — a topic tag through its descriptor's `evidence_fact_ids` (which the engine emits as exactly
 * the `topic_tag` fact whose value IS the tag), a slot value through its own sibling `fact_id`. A mislabelled
 * system path has no such sibling binding to match through, so it fails T-VERB-1 even though mislabelling would
 * silence the banned-output scanner. Equality with "some fact somewhere in the ledger" would prove nothing about
 * origin and is deliberately NOT the rule.
 */
export const APPLICANT_VERBATIM_PATHS: readonly string[] = [
  'entries[].descriptors.topic_tags[].value',
  'recommendations[].slots.*.value',
];

/** System/codebook/profile-authored text. Scanned by the banned-output registry. */
export const COPY_PATHS: readonly string[] = [
  // (ii) engine-pre-rendered RenderedString text — copy-provenance by construction, its authority the profile
  // release the engine stamped in at compute time.
  'overlay.rows[].prevalence.text',
  'overlay.rows[].documentation.text',
  // (iii) system-authored placeholder literals, drawn from the registered APPROVED_PLACEHOLDER_LITERALS.
  'recommendations[].slots.*.literal',
  // (iii cont.) closed-world scope prose — PAYLOAD/AUDIT-ONLY, NEVER RENDERED. These are raw engine-generated
  // strings with no copy id, so a rendered one could not resolve through registered copy. The UI renders the
  // registered CS-OMISSION-SCOPE template filled with the sibling `n_assessed` instead; `scope_description`
  // remains in the payload purely as the machine-checkable audit record that licenses the omission claim. It is
  // still scanned here, conservatively: it is system-authored and COULD leak into a render if this rule were ever
  // violated, and scanning costs nothing while catching exactly that regression.
  'domains[].not_shown.search_scope.scope_description',
  'overlay.rows[].search_scope.scope_description',
  'recommendations[].evidence.search_scope.scope_description',
  // C2b (R5.8 verify-round-2) — the per-entry closed-world scopes on L1 facet decisions and clarification flags.
  // Same rule as the three above: audit-only prose, scanned, never rendered (the UI renders CS-OMISSION-SCOPE-ENTRY).
  'entries[].responsibility.search_scope.scope_description',
  'entries[].completion.search_scope.scope_description',
  'entries[].continuity.search_scope.scope_description',
  'entries[].flags[].search_scope.scope_description',
  // (iv) profile-release-authored citation strings.
  'overlay.rows[].source_reference.pd_survey_citations[].citation',
  // (i) resolved-id copy: the id is the pointer, the registered template it resolves to is the copy.
  'domains[].domain_copy_string_id',
  'domains[].band_copy_string_id',
  'domains[].saturation_copy_string_id',
  'domains[].not_shown.copy_string_id',
  'domains[].not_shown.extended_copy_string_id',
  'entries[].responsibility.copy_string_id',
  'entries[].completion.copy_string_id',
  'entries[].continuity.copy_string_id',
  'entries[].facet_framing_copy_string_id',
  'entries[].observership_copy_string_id',
  'entries[].confidence.copy_string_id',
  'entries[].flags[].copy_string_id',
  'entries[].descriptors.collaboration.copy_string_id',
  'review_confidence.copy_string_id',
  'intended_use_copy_string_id',
  'input_surface_notice_copy_string_id',
  'presentation.sub_elements[].copy_string_id',
  'presentation.sub_elements[].band_copy_string_id',
  'presentation.overall_copy_string_id',
  'presentation.split_output_copy_string_id',
  'overlay.hedge_copy_string_id',
  'overlay.non_documentable_note_copy_string_id',
  'overlay.rows[].mapping_certainty_copy_string_id',
  'overlay.rows[].prevalence.copy_string_id',
  'overlay.rows[].documentation.copy_string_id',
  'recommendations[].kind_copy_string_id',
  'recommendations[].copy_string_id',
  /* R7.1 §6b — the convention panel. Engine-pre-rendered `RenderedString` text plus resolved-id pointers, exactly
     like the overlay rows above. `area_label` is the RATIFIED SIGNAL SET's own label: system/profile-authored copy
     whose authority is the signal set's ratification doc, so it is scanned as `copy` like every other such label
     (it is deliberately not `applicant_verbatim` — no applicant wrote it — and not `opaque_metadata`, because it
     is prose that renders). `positive_when`/`partial_when` never reach the payload at all. */
  'convention.convention.rows[].area_label',
  'convention.convention.rows[].card.text',
  'convention.convention.rows[].card.copy_string_id',
  'convention.convention.rows[].recover.text',
  'convention.convention.rows[].recover.copy_string_id',
  'convention.convention.rows[].source.text',
  'convention.convention.rows[].source.copy_string_id',
  'convention.convention.rows[].ratification_doc',
  // Audit-only closed-world prose, scanned and NEVER rendered — the surface renders `CS-OMISSION-SCOPE` filled
  // with the sibling `n_assessed` instead, inside "show source" (spec §4, Sol edit c).
  'convention.convention.rows[].search_scope.scope_description',
  'convention.claim_copy_string_id',
  'convention.footer_copy_string_id',
  'convention.convention.heading_copy_string_id',
  'convention.convention.hedge_copy_string_id',
  'convention.convention.empty_copy_string_id',
  'convention.surveys.heading_copy_string_id',
  'convention.surveys.hedge_copy_string_id',
  'convention.surveys.empty_copy_string_id',
  'convention.corpus.heading_copy_string_id',
  'convention.corpus.hedge_copy_string_id',
  'convention.corpus.empty_copy_string_id',
];

/** Fact/entry/unit id POINTERS — not themselves display text. */
export const EVIDENCE_REFERENCE_PATHS: readonly string[] = [
  'entries[].responsibility.evidence_fact_ids[]',
  'entries[].completion.evidence_fact_ids[]',
  'entries[].continuity.evidence_fact_ids[]',
  'domains[].evidence_fact_ids[]',
  'domains[].fully_documented_unit_ids[]',
  'domains[].assigned_unit_ids[]',
  'domains[].qualifying_entry_ids[]',
  // §4.6 / R5.5 — the entry `CS-WHAT-WOULD-CHANGE`'s `{x}` names. An ENTRY ID, exactly like the ids above it: the
  // surface resolves it to the applicant's own entry label and renders that label as `applicant_verbatim`, so the
  // id itself is a pointer and never display text. Classified here rather than as `opaque_metadata` for that
  // reason — it is the same kind of value as `qualifying_entry_ids[]`, arrived at by a different rule.
  'domains[].documentation_target_entry_id',
  'entries[].descriptors.venue_or_award_scopes[].evidence_fact_ids[]',
  'entries[].descriptors.recognition_types[].evidence_fact_ids[]',
  'entries[].descriptors.topic_tags[].evidence_fact_ids[]',
  'entries[].descriptors.trajectory[].evidence_fact_ids[]',
  'entries[].descriptors.recurrence.evidence_fact_ids[]',
  'entries[].descriptors.dissemination_event_types[].evidence_fact_ids[]',
  'entries[].descriptors.collaboration.evidence_fact_id',
  'presentation.sub_elements[].evidence_layout_fact_ids[]',
  'overlay.rows[].evidence_entry_ids[]',
  'overlay.rows[].evidence_fact_ids[]',
  'recommendations[].evidence.fact_ids[]',
  'recommendations[].slots.*.fact_id',
  'entries[].entry_id',
  'entries[].flags[].entry_id',
  'recommendations[].entry_id',
  'domains[].not_shown.search_scope.entry_ids_assessed[]',
  'overlay.rows[].search_scope.entry_ids_assessed[]',
  'recommendations[].evidence.search_scope.entry_ids_assessed[]',
  'entries[].responsibility.search_scope.entry_ids_assessed[]',
  'entries[].completion.search_scope.entry_ids_assessed[]',
  'entries[].continuity.search_scope.entry_ids_assessed[]',
  'entries[].flags[].search_scope.entry_ids_assessed[]',
  // R7.1 §6b — pointers, resolved by the same "show source" walk the overlay rows use.
  'convention.convention.rows[].evidence_entry_ids[]',
  'convention.convention.rows[].evidence_fact_ids[]',
  'convention.convention.rows[].search_scope.entry_ids_assessed[]',
];

/**
 * Displayed or displayable system identifiers, closed enums, booleans and numerics — not authored prose, not
 * applicant text, but values that CAN reach the screen and are therefore MAPPED, not omitted.
 *
 * FOUR OF THESE WERE ONCE CLASSIFIED `applicant_verbatim` and were moved here (a strictly SAFER direction — they
 * move INTO the scanner's scope): the venue/award scopes, recognition types, dissemination event types, and
 * recurrence frequency are extractor-NORMALIZED enum-like strings, not verbatim prose. The dissemination case also
 * made the old classification unsatisfiable: the engine emits `venue_scope` while citing a fact whose materialized
 * value is `event_type`, so byte-equality against the cited evidence fails on perfectly valid output.
 */
export const OPAQUE_METADATA_PATHS: readonly string[] = [
  'overlay.specialty',
  'overlay.cycle',
  'overlay.rows[].criterion_id',
  'overlay.rows[].source_reference.source_kind',
  'overlay.rows[].source_reference.corpus_release',
  'overlay.rows[].source_reference.cycle',
  'overlay.rows[].source_reference.last_verified',
  'overlay.rows[].source_reference.program_count',
  'overlay.rows[].source_reference.pd_survey_citations[].year',
  'overlay.rows[].prevalence_class',
  'overlay.rows[].documentation_class',
  'overlay.rows[].n_programs',
  'overlay.rows[].N_specialty',
  'overlay.rows[].criterion_mapping_certainty',
  'overlay.rows[].profile_release_id',
  'domains[].domain',
  'domains[].band',
  'entries[].responsibility.level',
  'entries[].completion.level',
  'entries[].continuity.level',
  'entries[].continuity.also_12_months_or_more',
  'entries[].domains[]',
  'entries[].signals_incomplete',
  'entries[].confidence.level',
  'review_confidence.level',
  'entries[].flags[].flag_type',
  'entries[].flags[].severity',
  // R5.8 — the entry's expectation profile: closed enums / system ids the surface BRANCHES on (CS-FACET-NA) and
  // never prints as prose. `sub_type` is an extractor-normalized enum value or a span-kind token, never applicant text.
  'entries[].expectations.profile_id',
  'entries[].expectations.sub_type',
  'entries[].expectations.responsibility',
  'entries[].expectations.completion',
  'entries[].expectations.continuity',
  'entries[].descriptors.trajectory[].value.domain',
  'entries[].descriptors.trajectory[].value.years[]',
  'entries[].descriptors.recurrence.value.date_start',
  'entries[].descriptors.recurrence.value.date_end',
  // The four reclassified normalized/closed-enum descriptor values.
  'entries[].descriptors.venue_or_award_scopes[].value',
  'entries[].descriptors.recognition_types[].value',
  'entries[].descriptors.dissemination_event_types[].value',
  'entries[].descriptors.recurrence.value.frequency',
  'presentation.overall',
  'presentation.sub_elements[].sub_element',
  'presentation.sub_elements[].band',
  'presentation.sub_elements[].applicable_items',
  'presentation.sub_elements[].satisfying_items',
  'presentation.sub_elements[].excluded_from_aggregation',
  'recommendations[].kind',
  'recommendations[].horizon',
  // A two-part copy POINTER: the identifier itself is opaque metadata; the registered PHRASING_TEMPLATES text it
  // selects — which the recommendations panel DOES render — is scanned as `copy`, exactly like every
  // `*_copy_string_id` pointer's resolved template. "Never templated from" governs only the raw id string.
  'recommendations[].template_id',
  'recommendations[].evidence.kind',
  'recommendations[].slots.*.kind',
  'domains[].not_shown.search_scope.claim_type',
  'domains[].not_shown.search_scope.n_assessed',
  'domains[].not_shown.search_scope.extractor_version',
  'overlay.rows[].search_scope.claim_type',
  'overlay.rows[].search_scope.n_assessed',
  'overlay.rows[].search_scope.extractor_version',
  'recommendations[].evidence.search_scope.claim_type',
  'recommendations[].evidence.search_scope.n_assessed',
  'recommendations[].evidence.search_scope.extractor_version',
  'entries[].responsibility.search_scope.claim_type',
  'entries[].responsibility.search_scope.n_assessed',
  'entries[].responsibility.search_scope.extractor_version',
  'entries[].completion.search_scope.claim_type',
  'entries[].completion.search_scope.n_assessed',
  'entries[].completion.search_scope.extractor_version',
  'entries[].continuity.search_scope.claim_type',
  'entries[].continuity.search_scope.n_assessed',
  'entries[].continuity.search_scope.extractor_version',
  'entries[].flags[].search_scope.claim_type',
  'entries[].flags[].search_scope.n_assessed',
  'entries[].flags[].search_scope.extractor_version',
  // The add-list envelope/identity fields.
  'read_id',
  'read_revision',
  'ledger_id',
  'ledger_version',
  'correction_revision',
  'assessed_at',
  'overlay_release_id',
  'scoring_version',
  'mode',
  'input_surface',
  // The three SERVE-TIME envelope fields. `overlay_status` moved here from the stored add-list in the rev-14
  // amendment (R5.2 round-2 finding 2); its provenance is unchanged — a closed enum the surface BRANCHES on and
  // never prints, displayable in principle and therefore mapped, not omitted, and scanned like every opaque value.
  'freshness',
  'stale_reason[]',
  'overlay_status',
  // R7.1 §6b — system identifiers, closed enums and booleans the surface BRANCHES on and never prints as prose.
  'convention.specialty',
  'convention.signal_set_version',
  'convention.convention.owner_reviewed',
  'convention.surveys.owner_reviewed',
  'convention.corpus.owner_reviewed',
  'convention.convention.rows[].signal_id',
  'convention.convention.rows[].binding',
  'convention.convention.rows[].ct_a_mapping',
  'convention.convention.rows[].status',
  'convention.convention.rows[].search_scope.claim_type',
  'convention.convention.rows[].search_scope.n_assessed',
  'convention.convention.rows[].search_scope.extractor_version',
  'band_changes[].subject',
  'band_changes[].before_band',
  'band_changes[].after_band',
];

/**
 * The complete map. There is no fifth provenance and no payload path this map fails to classify — T-DTO-1 and
 * T-BAN-1 jointly assert exactly that, over BRANCH-COMPLETE fixtures, so no conditionally-present path can escape
 * the sweep by simply never occurring.
 */
export const PROVENANCE_MAP: ReadonlyMap<string, Provenance> = new Map<string, Provenance>([
  ...COPY_PATHS.map((p) => [p, 'copy'] as [string, Provenance]),
  ...APPLICANT_VERBATIM_PATHS.map((p) => [p, 'applicant_verbatim'] as [string, Provenance]),
  ...EVIDENCE_REFERENCE_PATHS.map((p) => [p, 'evidence_reference'] as [string, Provenance]),
  ...OPAQUE_METADATA_PATHS.map((p) => [p, 'opaque_metadata'] as [string, Provenance]),
]);

export function provenanceOf(path: string): Provenance | null {
  return PROVENANCE_MAP.get(path) ?? null;
}

/** One primitive value on the payload, with the normalized path that produced it. */
export interface WalkedValue {
  path: string;
  value: string | number | boolean;
}

/**
 * Walk every primitive-bearing path on a served payload, normalizing array indices to `[]` and the dynamic keys of
 * `slots` records to `*`, so a walked path is directly comparable against the provenance map's fixed patterns.
 *
 * Nulls are skipped: a null-valued path renders nothing and carries no string to scan, and requiring a fixture to
 * classify the ABSENCE of a value would make branch-completeness impossible to satisfy rather than merely strict.
 */
export function walkPayloadValues(payload: unknown): WalkedValue[] {
  const out: WalkedValue[] = [];
  const visit = (node: unknown, path: string): void => {
    if (node === null || node === undefined) return;
    if (Array.isArray(node)) {
      for (const item of node) visit(item, `${path}[]`);
      return;
    }
    if (typeof node === 'object') {
      // `slots` is a Record<string, SlotValue> whose KEYS are per-recommendation and therefore not enumerable in a
      // fixed map. Normalizing them to `*` is what lets the two slot paths be pinned at all.
      const dynamicKeys = path.endsWith('.slots');
      for (const [k, v] of Object.entries(node as Record<string, unknown>)) {
        visit(v, `${path}${path ? '.' : ''}${dynamicKeys ? '*' : k}`);
      }
      return;
    }
    out.push({ path, value: node as string | number | boolean });
  };
  visit(payload, '');
  return out;
}
